import { Log, UserManager, WebStorageStateStore, type User } from "oidc-client-ts";
import type { AppMountContext } from "@tc-superpods/app-contract";
import {
  anonymousToken,
  mode,
  normalizePathname,
  oidcAuthority,
  oidcClientId,
  oidcRedirectPath,
  oidcSilentRedirectPath,
  oidcScope
} from "./config.js";
import type { AuthStatus, RegistryEntry, TenantSummary } from "./types.js";

const pendingAuthReturnToStorageKey = "kn-auth-return-to";

export type SetAuth = (status: AuthStatus, message: string) => void;

export function createUserManager(): UserManager {
  Log.setLogger(console);
  Log.setLevel(mode === "production" ? Log.ERROR : Log.WARN);

  return new UserManager({
    authority: oidcAuthority,
    client_id: oidcClientId,
    redirect_uri: `${window.location.origin}${oidcRedirectPath}`,
    silent_redirect_uri: `${window.location.origin}${oidcSilentRedirectPath}`,
    response_type: "code",
    scope: oidcScope,
    loadUserInfo: true,
    automaticSilentRenew: true,
    monitorSession: false,
    userStore: new WebStorageStateStore({ store: window.sessionStorage })
  });
}

export async function resolveAuthenticatedUser(
  manager: UserManager,
  setAuth: SetAuth
): Promise<User | null> {
  if (isAuthCallbackPath(window.location.pathname)) {
    await setAuthAndPaint(setAuth, "callback", "Completing sign-in.");
    await waitForCallbackStateObservationWindow();

    try {
      const callbackUser = await manager.signinCallback();
      const authenticatedUser = callbackUser ?? (await manager.getUser());

      if (!authenticatedUser) {
        throw new Error("Authentication callback did not produce a user session.");
      }

      clearPendingAuthReturnTo();
      window.history.replaceState(null, "", getReturnToPath(authenticatedUser));
      return authenticatedUser;
    } catch (error) {
      if (!isMissingCallbackStateError(error)) {
        throw error;
      }

      const returnTo = getPendingAuthReturnTo() ?? "/";
      window.history.replaceState(null, "", returnTo);
      await startSigninRedirect(manager, setAuth, returnTo);
      return null;
    }
  }

  const existingUser = await manager.getUser();
  if (existingUser && !existingUser.expired) {
    return existingUser;
  }

  await startSigninRedirect(manager, setAuth, getRequestedPath());

  return null;
}

export async function triggerReauthentication(manager: UserManager, setAuth: SetAuth): Promise<void> {
  await startSigninRedirect(manager, setAuth, getRequestedPath());
}

export function createAnonymousIdentity(): AppMountContext["identity"] {
  return {
    userId: "public-user",
    name: "Public User",
    email: "public@korenext.local",
    token: async () => anonymousToken
  };
}

export function createIdentityFromUser(
  manager: UserManager,
  authenticatedUser: User,
  onReauth: () => void
): AppMountContext["identity"] {
  const userId = readRequiredStringClaim(authenticatedUser.profile.sub, "sub");
  const name =
    readOptionalStringClaim(authenticatedUser.profile.name) ??
    readOptionalStringClaim(authenticatedUser.profile.preferred_username) ??
    readOptionalStringClaim(authenticatedUser.profile.email) ??
    userId;
  const email =
    readOptionalStringClaim(authenticatedUser.profile.email) ??
    readOptionalStringClaim(authenticatedUser.profile.preferred_username) ??
    "unknown@korenext.local";

  return {
    userId,
    name,
    email,
    token: async () => {
      const currentUser = await manager.getUser();

      if (currentUser && !currentUser.expired && typeof currentUser.access_token === "string") {
        return currentUser.access_token;
      }

      try {
        const renewedUser = await manager.signinSilent({ forceIframeAuth: true });
        if (renewedUser && !renewedUser.expired && typeof renewedUser.access_token === "string") {
          return renewedUser.access_token;
        }
      } catch {
        // Iframe renewal failed or timed out; the hard-redirect fallback below restores auth.
      }

      await manager.removeUser();
      onReauth();
      throw new Error("Authentication required.");
    }
  };
}

export function createTenantContext(
  selectedTenant: TenantSummary,
  accessToken: string | (() => Promise<string>),
  registry: ReadonlyArray<RegistryEntry>
): AppMountContext["tenant"] {
  const features = Object.fromEntries(registry.map((entry) => [entry.id, true]));

  return {
    id: selectedTenant.id,
    name: selectedTenant.name,
    features,
    token: async () => typeof accessToken === "function" ? accessToken() : accessToken
  };
}

export function createResolvedTenantContext(
  selectedTenant: TenantSummary,
  registry: ReadonlyArray<RegistryEntry>,
  tenantAccessToken: string | null,
  tenantToken: (() => Promise<string>) | null = null
): AppMountContext["tenant"] {
  return createTenantContext(
    selectedTenant,
    tenantToken ?? tenantAccessToken ?? (() => Promise.reject(new Error("Tenant token provider is unavailable."))),
    registry
  );
}

export function isAuthCallbackPath(pathname: string): boolean {
  return normalizePathname(pathname) === oidcRedirectPath;
}

async function startSigninRedirect(
  manager: UserManager,
  setAuth: SetAuth,
  returnTo: string
): Promise<void> {
  setPendingAuthReturnTo(returnTo);
  await setAuthAndPaint(setAuth, "redirecting", "Redirecting to sign-in.");
  await manager.signinRedirect({
    state: {
      returnTo
    }
  });
}

function isMissingCallbackStateError(error: unknown): boolean {
  return error instanceof Error && error.message.includes("No matching state found in storage");
}

function setPendingAuthReturnTo(returnTo: string): void {
  try {
    window.localStorage.setItem(pendingAuthReturnToStorageKey, returnTo);
  } catch {
    // Best-effort recovery hint only; oidc-client-ts still owns the primary state store.
  }
}

function getPendingAuthReturnTo(): string | null {
  try {
    const returnTo = window.localStorage.getItem(pendingAuthReturnToStorageKey);
    return returnTo && returnTo.startsWith("/") ? returnTo : null;
  } catch {
    return null;
  }
}

function clearPendingAuthReturnTo(): void {
  try {
    window.localStorage.removeItem(pendingAuthReturnToStorageKey);
  } catch {
    // Best-effort cleanup only; blocked storage should not fail authenticated startup.
  }
}

function waitForNextPaint(): Promise<void> {
  return new Promise((resolve) => {
    window.requestAnimationFrame(() => resolve());
  });
}

async function setAuthAndPaint(setAuth: SetAuth, status: AuthStatus, message: string): Promise<void> {
  await waitForMacrotask();
  setAuth(status, message);
  await waitForNextPaint();
}

function waitForMacrotask(): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, 0);
  });
}

function waitForCallbackStateObservationWindow(): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, mode === "test" ? 300 : 50);
  });
}

function getRequestedPath(): string {
  const { pathname, search, hash } = window.location;

  return `${pathname}${search}${hash}`;
}

function getReturnToPath(authenticatedUser: User): string {
  const userState = authenticatedUser.state;

  if (isRecord(userState) && typeof userState.returnTo === "string" && userState.returnTo.startsWith("/")) {
    return userState.returnTo;
  }

  return "/";
}

function readOptionalStringClaim(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function readRequiredStringClaim(value: unknown, claimName: string): string {
  const claimValue = readOptionalStringClaim(value);

  if (!claimValue) {
    throw new Error(`Authenticated user was missing the required ${claimName} claim.`);
  }

  return claimValue;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
